import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import LineItemsCard from 'src/components/LineItemsCard';
import LoadingIndicator from 'src/components/LoadingIndicator';
import { ShipmentBanner } from 'src/components/ShipmentBanner';
import VideoModal from 'src/components/VideoModal';
import emptyImg from 'src/images/empty.svg';
import heavyBoxRedImg from 'src/images/heavy-box-red.svg';
import logo from 'src/images/logo-md.png';
import { SignupForm } from 'src/pages/Auth/signup';
import { Container, useApp } from 'straightline-ui';
import {
  AddressCardPreview,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Icon,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  ListGroup,
  ListGroupItem,
  Modal,
  ModalBody,
  Row,
  Stripes
} from 'straightline-ui';
import { formatMoney } from 'straightline-utils/accounting';
import {
  ACCESSORIAL_CODES,
  isBooked,
  PROJECT_44
} from 'straightline-utils/constants';
import { differenceInSeconds, formatDate } from 'straightline-utils/time';

const OTHER_CODES = PROJECT_44.ACCESSORIAL_CODES.filter((code) => {
  const isPickup = ACCESSORIAL_CODES.PICKUP.find(
    (pickup) => pickup.value === code.value
  );
  const isDelivery = ACCESSORIAL_CODES.DELIVERY.find(
    (pickup) => pickup.value === code.value
  );
  return !isPickup && !isDelivery;
});

export default function LoadBoardPreview({ match, location }) {
  const app = useApp();
  const history = useHistory();
  const [loginAttempted, setLoginAttempted] = useState(false);

  const token = location.query.token;

  const {
    result: shipment,
    isLoading,
    isError,
    error
  } = app.service('api/shipment-previews').useGet(
    token,
    { $include: ['pickup_address', 'delivery_address'] },
    {
      enabled: !!token,
      refetchInterval: (result) => {
        if (!result) {
          return false;
        }
        const created = differenceInSeconds(new Date(), result.created_at);
        if (created <= 15) {
          return 3000;
        }
        if (created <= 60) {
          return 8000;
        }
        if (created <= 60 * 5) {
          return 15000;
        }
        return 60000;
      }
    }
  );

  useEffect(() => {
    const attemptLogin = async () => {
      try {
        const token = window.localStorage.getItem('straightline-token');
        if (token) {
          await app.authenticate();
          history.push(`/shipment-matches/${shipment.id}`);
        }
        setLoginAttempted(true);
      } catch (error) {
        setLoginAttempted(true);
      }
    };

    if (shipment && !loginAttempted) {
      attemptLogin();
    }
  }, [app, history, shipment, loginAttempted]);

  if (isLoading || !loginAttempted) {
    return (
      <div className="d-flex vh-100 align-items-center">
        <LoadingIndicator delay={250} />
      </div>
    );
  }

  if (isError || !token) {
    return <Error error={error} />;
  }

  return (
    <React.Fragment>
      <Stripes />
      <Container fluid className="fixed-top bg-white shadow-sm bg-white">
        <Row className="align-items-center py-2">
          <Col xs="4">
            <img src={logo} style={{ maxWidth: 138 }} alt="Straightline Logo" />
          </Col>
          <Col xs="8" className="ml-auto text-right">
            <a
              href="https://www.bookstraightline.com/carriers"
              target="_blank"
              rel="noreferrer"
              className="btn btn-white"
            >
              <Icon type="help-circle" className="mr-2" />
              Learn More
            </a>
          </Col>
        </Row>
      </Container>
      <Container className="pb-3" style={{ marginTop: 53 }}>
        <div className="header text-white">
          <div className="header-body">
            <Row className="align-items-center justify-content-between">
              <Col lg="auto" className="mb-3 mb-lg-0">
                <div className="font-weight-bold">
                  <span className="">LOAD #: </span>
                  {shipment.id}
                </div>
                <Row className="align-items-center mb-n1">
                  <Col
                    xs="auto"
                    className="mb-2 pr-0 align-items-center d-flex"
                  >
                    <h1 className="mb-0 font-weight-bold d-inline-block mr-3">{`${shipment.pickup_address.city}, ${shipment.pickup_address.state}`}</h1>
                    <Icon
                      type="arrow-right"
                      style={{ fontSize: 24 }}
                      className="text-success lead d-inline-block font-weight-bold"
                    />
                  </Col>
                  <Col xs="auto" className="mb-2">
                    <h1 className="mb-0 font-weight-bold d-inline-block">{`${shipment.delivery_address.city}, ${shipment.delivery_address.state}`}</h1>
                  </Col>
                </Row>
              </Col>
              <Col lg="auto">
                <Row className="mb-n3">
                  <Col md="auto" className="p-md-0 mr-md-3 mb-3">
                    <VideoModal className="btn-block" />
                  </Col>
                  <Col md="auto" className="p-md-0 mr-md-3 mb-3">
                    <ContactModal shipment={shipment} />
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>
        </div>
        {isBooked(shipment) ? (
          <Preview shipment={shipment} />
        ) : (
          <AlreadyMatched shipment={shipment} />
        )}
      </Container>
    </React.Fragment>
  );
}

const ContactModal = ({ shipment }) => {
  const [open, setOpen] = useState(false);
  const toggle = () => setOpen((open) => !open);

  return (
    <React.Fragment>
      <Modal
        centered
        isOpen={open}
        toggle={toggle}
        className="mx-auto"
        style={{ maxWidth: 425, width: '95%' }}
      >
        {() => (
          <ModalBody>
            <h2>We're here for you.</h2>
            <p>
              Straightline has a team of logistics professionals ready to help.
              Give us a shout and we will get back to you ASAP.
            </p>
            <p>
              <Icon type="phone" className="mr-3" />
              <a href={`tel:${process.env.REACT_APP_SUPPORT_PHONE}`}>
                {process.env.REACT_APP_SUPPORT_PHONE}
              </a>
            </p>
            <p>
              <Icon type="mail" className="mr-3" />
              <a
                href={`mailto:${
                  process.env.REACT_APP_SUPPORT_EMAIL
                }?subject=Shipment #${shipment?.id || 'NA'}`}
              >
                {process.env.REACT_APP_SUPPORT_EMAIL}
              </a>
            </p>
            <div className="mt-5">
              <Button
                block
                color="white"
                className="mr-3"
                type="button"
                onClick={toggle}
              >
                Close
              </Button>
            </div>
          </ModalBody>
        )}
      </Modal>
      <Button color="white" onClick={toggle} block>
        <Icon type="send" className="mr-2" />
        Contact
      </Button>
    </React.Fragment>
  );
};

const Preview = ({ shipment }) => {
  const [success, setSuccess] = useState(false);
  const isToday = shipment.pickup_date === formatDate(new Date(), 'dateOnly');

  return (
    <React.Fragment>
      <Card>
        <CardBody>
          <Row>
            <Col lg="6" className="mb-4 mb-lg-0 pr-lg-4">
              <div className="">
                <ShipmentBanner shipment={shipment} />
              </div>
              <div className="">
                <ListGroup className="mx-n3 mx-sm-0 list-group list-group-flush list-group-activity mb-3">
                  <ListGroupItem className="py-3">
                    <Row>
                      <Col xs="auto">
                        <div className="avatar avatar-sm">
                          <div className="avatar-title font-size-lg rounded-circle bg-light text-dark">
                            <Icon type="clipboard" />
                          </div>
                        </div>
                      </Col>
                      <Col className="ml-n2">
                        <div className="font-weight-bold">
                          {shipment.rate.service_level.name}
                        </div>
                        {shipment.rate.service_level.code === 'TEM' && (
                          <div className="font-weight-bold text-primary">
                            ** This shipment requires two drivers **
                          </div>
                        )}
                        <div>
                          <Icon type="clock" className="mr-3 text-secondary" />
                          {shipment.rate.service_level.description}
                        </div>
                        <div>
                          <Icon type="truck" className="mr-3 text-secondary" />
                          {shipment.truck.label}
                        </div>
                        <AccessorialCodes
                          shipment={shipment}
                          codes={OTHER_CODES}
                        />
                        {shipment.accessorial_codes?.includes('HAZM') && (
                          <div className="font-weight-bold text-warning mt-2 d-flex">
                            <Icon type="alert-triangle" className="mr-2" />
                            This load contains hazardous materials and requires
                            Hazmat certification
                          </div>
                        )}
                        {shipment.accessorial_codes?.includes('TANK') && (
                          <div className="font-weight-bold text-warning mt-2 d-flex">
                            <Icon type="alert-triangle" className="mr-2" />
                            This load requires tanker endorsement certification
                          </div>
                        )}
                      </Col>
                    </Row>
                  </ListGroupItem>
                  <ListGroupItem className="py-3">
                    <Row>
                      <Col xs="auto">
                        <div className="avatar avatar-sm">
                          <div className="avatar-title font-size-lg rounded-circle bg-light text-dark">
                            <Icon type="truck" />
                          </div>
                        </div>
                      </Col>
                      <Col className="ml-n2">
                        <div>
                          <AddressCardPreview
                            map={false}
                            address={shipment.pickup_address}
                            date={shipment.pickup_date}
                            showCurrentTime={false}
                            showStreet={false}
                            showWeather
                            showAppointment
                            showDate
                          />
                          <div>
                            <AccessorialCodes
                              shipment={shipment}
                              codes={ACCESSORIAL_CODES.PICKUP}
                            />
                          </div>
                          {isToday && (
                            <div className="text-primary font-weight-bold mt-2">
                              ** This load picks up today! **
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>
                  </ListGroupItem>
                  <ListGroupItem className="py-3">
                    <Row>
                      <Col xs="auto">
                        <div className="avatar avatar-sm">
                          <div className="avatar-title font-size-lg rounded-circle bg-light text-dark">
                            <Icon type="map-pin" />
                          </div>
                        </div>
                      </Col>
                      <Col className="ml-n2">
                        <div>
                          <AddressCardPreview
                            map={false}
                            address={shipment.delivery_address}
                            date={shipment.due_date}
                            showCurrentTime={false}
                            showStreet={false}
                            showWeather
                            showAppointment
                            showDate
                          />
                          <div>
                            <AccessorialCodes
                              shipment={shipment}
                              codes={ACCESSORIAL_CODES.DELIVERY}
                            />
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </ListGroupItem>
                </ListGroup>
                <LineItemsCard shipment={shipment} className="shadow-none" />
                <h5 className="text-uppercase text-muted font-weight-bold">
                  Notes
                </h5>
                <p className="pre-line mb-4">
                  {shipment.notes || <span className="text-muted">NA</span>}
                </p>
              </div>
              <hr className="mx-n4" />
              <ProfitCalculator shipment={shipment} />
            </Col>
            <Col lg="6" className="pl-lg-4 border-lg-left my-lg-n4 py-lg-4">
              <hr className="d-lg-none mb-5 mx-n4" />
              <h1 className="text-primary mb-4">
                Signup to counteroffer or book this load.
              </h1>
              <div className="mb-2">
                Straightline is a 100% FREE truckload booking and payments
                service that offers unparalleled transparency and speed to the
                logistics industry.
              </div>
              <div className="text-danger mb-4">
                Straightline does not accept offers over the phone or via email.
                You must sign up to make offers or book loads.
              </div>
              {success ? (
                <div className="alert alert-primary animated fadeIn fast">
                  <h2 className="">Success!</h2>
                  <p>
                    You have successfully signed up for Straightline. Check your
                    email to validate your account. If you do not see an email,
                    be sure to check your spam filter. Once validated, you can
                    make offers or book this load.
                  </p>
                </div>
              ) : (
                <SignupForm
                  matchId={shipment.id}
                  handleSuccess={() => setSuccess(true)}
                />
              )}
            </Col>
          </Row>
        </CardBody>
      </Card>
    </React.Fragment>
  );
};

const AccessorialCodes = ({ shipment, codes }) => {
  const items = codes.filter((code) => {
    return shipment.accessorial_codes?.includes(code.value);
  });

  if (!items.length) {
    return null;
  }

  return (
    <div className="mt-3">
      {items.map((item) => {
        return (
          <Badge
            pill
            key={item.value}
            color="soft-primary"
            className="mr-2 mb-1"
          >
            {item.label}
          </Badge>
        );
      })}
    </div>
  );
};

const ProfitCalculator = ({ shipment }) => {
  const [data, setData] = useState({
    cpm: 0,
    deadhead: 0
  });

  const miles = shipment.travel.miles + data.deadhead;
  const carrierCost = data.cpm * miles;

  return (
    <div className="">
      <div className="mb-4">
        Enter your fixed cost per mile to use the profit calculator below.
      </div>
      <Row className="mb-4">
        <Col xs="6" className="">
          <h5 className="text-uppercase text-muted mb-2 font-weight-bold">
            Fixed CPM
          </h5>
          <InputGroup className="input-group-merge">
            <Input
              type="money"
              className="form-control-prepended form-control-appended"
              placeholder="1.50"
              value={data.cpm || undefined}
              onChange={({ target: { value } }) => {
                value = value || 0;
                setData({
                  ...data,
                  cpm: Number(value)
                });
              }}
            />
            <InputGroupAddon addonType="prepend">
              <InputGroupText>
                <Icon type="dollar-sign" />
              </InputGroupText>
            </InputGroupAddon>
            <InputGroupAddon addonType="append">
              <InputGroupText>/mile</InputGroupText>
            </InputGroupAddon>
          </InputGroup>
        </Col>
        <Col xs="6" className="">
          <h5 className="text-uppercase text-muted mb-2 font-weight-bold">
            Deadhead
          </h5>
          <InputGroup className="input-group-merge">
            <Input
              type="number"
              className="form-control-appended"
              min="0"
              placeholder="25"
              value={data.deadhead || undefined}
              onChange={({ target: { value } }) => {
                value = value || 0;
                setData({
                  ...data,
                  deadhead: Number(value)
                });
              }}
            />
            <InputGroupAddon addonType="append">
              <InputGroupText>miles</InputGroupText>
            </InputGroupAddon>
          </InputGroup>
        </Col>
      </Row>
      <Row className="">
        <Col className="border-right">
          <h5 className="text-uppercase text-muted mb-1 font-weight-bold ">
            Payout
          </h5>
          <div>{formatMoney(shipment.rate.price.carrier_price)}</div>
          <div>
            {formatMoney(shipment.rate.price.carrier_price / miles)}
            <span className="text-secondary small">/mile</span>
          </div>
        </Col>
        <Col className="border-right">
          <h5 className="text-uppercase text-muted mb-1 font-weight-bold ">
            Cost
          </h5>
          <div className="animated fadeIn fast" key={`cost-${data.cpm}`}>
            {formatMoney(carrierCost)}
          </div>
          <div className="animated fadeIn fast" key={`cost-cpm-${data.cpm}`}>
            {formatMoney(carrierCost / miles)}
            <span className="text-secondary small">/mile</span>
          </div>
        </Col>
        <Col className="">
          <h5 className="text-uppercase text-muted mb-1 font-weight-bold ">
            Profit
          </h5>
          <div className="animated fadeIn fast" key={`profit-${data.cpm}`}>
            {formatMoney(shipment.rate.price.carrier_price - carrierCost)}
          </div>
          <div className="animated fadeIn fast" key={`profit-cpm-${data.cpm}`}>
            {formatMoney(
              (shipment.rate.price.carrier_price - carrierCost) / miles
            )}
            <span className="text-secondary small">/mile</span>
          </div>
        </Col>
      </Row>
    </div>
  );
};

const AlreadyMatched = ({ shipment }) => {
  return (
    <Card>
      <CardBody>
        <Row>
          <Col lg="6" className="pr-lg-4">
            <img
              src={heavyBoxRedImg}
              className="img-fluid"
              alt="Shipment not available"
            />
          </Col>
          <Col lg="6" className="pl-lg-4 border-lg-left my-lg-n4 py-lg-4">
            <hr className="d-lg-none mb-5" />
            <div style={{ maxWidth: 250 }}>
              <img
                src={logo}
                className="img-fluid mb-3"
                alt="Straightline Logo"
              />
            </div>
            <div className="bg-danger text-white p-3 rounded mb-3">
              This load is no longer available. Most likely another carrier
              accepted the load before you. Sign up to get notifications of
              loads like this one.
            </div>
            <p className="">
              Straightline is a real-time truckload booking, matching, and
              payments service that offers unparalleled transparency and speed
              to the logistics industry. Sign up and gain access to real time
              load boards, active notifications, digital rate confirmations and
              more.
            </p>
            <Row className="mb-n2">
              <Col md="auto" className="p-md-0 mr-md-3 mb-2">
                <VideoModal className="btn-block" />
              </Col>
              <Col md="auto" className="p-md-0 mr-md-3 mb-2">
                <ContactModal shipment={shipment} />
              </Col>
              <Col md="auto" className="p-md-0 mb-2">
                <Button to="/signup" color="primary" block>
                  <Icon type="truck" className="mr-2" />
                  Sign Up for Free
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

const Error = ({ error }) => {
  return (
    <Container>
      <Row className="align-items-center justify-content-center vh-100">
        <Col lg="6" className="text-center">
          <img
            src={emptyImg}
            alt="Not Found"
            className="img-fluid animated fadeIn faster mb-5"
          />
          {error?.code === 404 ? (
            <h1>Could not find that load</h1>
          ) : (
            <h1>{error?.message || 'Could not find that load'}</h1>
          )}
          <Button
            to="/shipment-matches"
            color="primary"
            size="lg"
            className="mr-3"
          >
            <Icon type="clipboard" className="mr-2" />
            Load Board
          </Button>
          <Button to="/support" color="secondary" size="lg">
            <Icon type="send" className="mr-2" />
            Contact Straightline
          </Button>
        </Col>
      </Row>
    </Container>
  );
};
